import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Globe, Mail, ShieldCheck, ShieldAlert, Loader2, Search, KeyRound,
  AlertTriangle, CheckCircle2, XCircle, Copy, Save, Fingerprint,
} from "lucide-react";

const SEV_STYLE = {
  critical: "text-sev-critical border-sev-critical/40",
  high: "text-sev-high border-sev-high/40",
  medium: "text-sev-medium border-sev-medium/40",
  low: "text-sev-low border-sev-low/40",
  ok: "text-emerald-400 border-emerald-500/40",
};

function StatusPill({ ok, warn, label, testid }) {
  const tone = ok ? "ok" : warn ? "medium" : "high";
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : XCircle;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] font-mono uppercase tracking-widest ${SEV_STYLE[tone]}`}
      data-testid={testid}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function RecordBlock({ title, icon: Icon, records, testid, empty = "sin registros" }) {
  return (
    <div className="border border-border bg-card/60" data-testid={testid}>
      <div className="p-4 border-b border-border flex items-center gap-2">
        <Icon className="w-4 h-4 text-primary" />
        <h3 className="font-display text-sm font-medium">{title}</h3>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          {records?.length || 0}
        </span>
      </div>
      <div className="p-3 max-h-48 overflow-auto">
        {(!records || records.length === 0) ? (
          <p className="text-xs text-muted-foreground font-mono italic">{empty}</p>
        ) : records.map((r, i) => (
          <div key={i} className="font-mono text-xs break-all py-1 border-b border-border/30 last:border-0">
            {r}
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailAuthCard({ title, present, record, issues, extra, testid }) {
  const isOk = present && (!issues || issues.length === 0 || issues.every(i => !i));
  const hasProblems = present && issues && issues.length > 0;
  return (
    <div className={`border ${isOk ? "border-emerald-500/40" : hasProblems ? "border-sev-medium/40" : "border-sev-high/40"} bg-card/60 p-5`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-primary" />
          <h3 className="font-display text-lg font-medium">{title}</h3>
        </div>
        <StatusPill
          ok={isOk}
          warn={hasProblems}
          label={present ? (isOk ? "OK" : "REVISAR") : "AUSENTE"}
          testid={`${testid}-status`}
        />
      </div>
      {record && (
        <div className="font-mono text-[11px] bg-black/40 border border-border/50 p-2 break-all mb-3">
          {record}
        </div>
      )}
      {extra}
      {issues && issues.length > 0 && (
        <ul className="text-xs space-y-1 mt-2">
          {issues.map(i => (
            <li key={i} className="flex items-center gap-2 text-sev-medium">
              <AlertTriangle className="w-3 h-3" />
              <code>{i}</code>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DnsAnalysis() {
  const [domain, setDomain] = useState("");
  const [selectors, setSelectors] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [typosquat, setTyposquat] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [typoLoading, setTypoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(
    () => sessionStorage.getItem("cibraxis:dns:client") || ""
  );

  useEffect(() => {
    api.get("/clients").then(r => {
      const list = r.data || [];
      setClients(list);
      if (!clientId && list.length > 0) {
        setClientId(list[0].id);
        sessionStorage.setItem("cibraxis:dns:client", list[0].id);
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const runAnalyze = async () => {
    if (!domain.trim()) return toast.error("Introduce un dominio");
    setAnalyzing(true);
    setAnalysis(null);
    setTyposquat(null);
    try {
      const dkim_selectors = selectors.split(",").map(s => s.trim()).filter(Boolean);
      const r = await api.post("/dns/analyze", { domain: domain.trim(), dkim_selectors, client_id: clientId || undefined });
      setAnalysis(r.data);
      toast.success("Análisis DNS completado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al analizar el dominio");
    } finally { setAnalyzing(false); }
  };

  const runTyposquat = async () => {
    if (!domain.trim()) return toast.error("Introduce un dominio");
    setTypoLoading(true);
    try {
      const r = await api.post("/dns/typosquat", { domain: domain.trim(), max_variants: 200 });
      setTyposquat(r.data);
      toast.success(`${r.data.registered} variantes registradas encontradas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al buscar typosquatting");
    } finally { setTypoLoading(false); }
  };

  const saveFindings = async () => {
    if (!clientId) return toast.error("Selecciona un cliente");
    if (!analysis && !typosquat) return toast.error("Ejecuta un análisis primero");
    setSaving(true);
    try {
      const r = await api.post("/dns/save-findings", {
        client_id: clientId,
        dns_analysis: analysis || null,
        typosquat_scan: typosquat || null,
      });
      toast.success(`${r.data.saved} findings guardados (${r.data.deduped || 0} duplicados omitidos)`);
      if (r.data.rating) {
        toast.success(`Nota DNS/Email: ${r.data.rating.dns_letter} ${r.data.rating.dns_score}/100 · Global ${r.data.rating.overall_letter} ${r.data.rating.overall_score}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const copy = (v) => {
    navigator.clipboard.writeText(v).then(() => toast.success("Copiado"));
  };

  const onClientChange = (id) => {
    setClientId(id);
    if (id) sessionStorage.setItem("cibraxis:dns:client", id);
    else sessionStorage.removeItem("cibraxis:dns:client");
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dns-analysis-page">
      <header>
        <div className="overline mb-2">DNS · Email Auth · Brand Protection</div>
        <h1 className="font-display text-4xl font-medium tracking-tight">Análisis DNS</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Registros A/MX/TXT/CNAME/NS/CAA · Políticas SPF, DMARC, DKIM, BIMI, MTA-STS · Typosquatting.
        </p>
      </header>

      <section className="border border-border bg-card/60 p-5 space-y-3" data-testid="dns-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="overline text-[10px] block mb-1">Dominio a analizar</label>
            <input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runAnalyze()}
              placeholder="ejemplo.com"
              className="w-full bg-background border border-border px-3 py-2 font-mono text-sm"
              data-testid="dns-domain-input"
            />
          </div>
          <div>
            <label className="overline text-[10px] block mb-1">Cliente destino (para guardar findings)</label>
            <select
              value={clientId}
              onChange={(e) => onClientChange(e.target.value)}
              className="w-full bg-background border border-border px-3 py-2 text-sm font-mono"
              data-testid="dns-client-select"
            >
              <option value="">— sin guardar —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="overline text-[10px] block mb-1">
            Selectores DKIM adicionales (opcional, coma-separados)
          </label>
          <input
            value={selectors}
            onChange={(e) => setSelectors(e.target.value)}
            placeholder="s1, k2, corporate"
            className="w-full bg-background border border-border px-3 py-2 font-mono text-xs"
            data-testid="dns-dkim-selectors"
          />
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            onClick={runAnalyze}
            disabled={analyzing}
            className="bg-primary text-primary-foreground px-4 py-2 text-sm font-mono uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="dns-analyze-btn"
          >
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Analizar DNS
          </button>
          <button
            onClick={runTyposquat}
            disabled={typoLoading || !domain.trim()}
            className="bg-card border border-border px-4 py-2 text-sm font-mono uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50 hover:border-primary/50"
            data-testid="dns-typosquat-btn"
          >
            {typoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
            Buscar typosquatting
          </button>
          {(analysis || typosquat) && clientId && (
            <button
              onClick={saveFindings}
              disabled={saving}
              className="ml-auto bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 px-4 py-2 text-sm font-mono uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="dns-save-findings-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar findings
            </button>
          )}
        </div>
      </section>

      {analysis && (
        <>
          <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dns-summary">
            <EmailAuthCard
              testid="dns-spf-card"
              title="SPF"
              present={analysis.spf.present}
              record={analysis.spf.record}
              issues={analysis.spf.issues}
              extra={
                <div className="text-[11px] font-mono text-muted-foreground">
                  Lookups: {analysis.spf.lookups} · All: <code>{analysis.spf.all_qualifier || "—"}</code>
                </div>
              }
            />
            <EmailAuthCard
              testid="dns-dmarc-card"
              title="DMARC"
              present={analysis.dmarc.present}
              record={analysis.dmarc.record}
              issues={analysis.dmarc.issues}
              extra={
                <div className="text-[11px] font-mono text-muted-foreground grid grid-cols-2 gap-1">
                  <span>Política: <code>{analysis.dmarc.policy || "—"}</code></span>
                  <span>Pct: <code>{analysis.dmarc.pct ?? "—"}%</code></span>
                  <span>RUA: <code>{analysis.dmarc.rua?.length || 0}</code></span>
                  <span>RUF: <code>{analysis.dmarc.ruf?.length || 0}</code></span>
                </div>
              }
            />
            <div className="border border-border bg-card/60 p-5" data-testid="dns-dkim-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-primary" />
                  <h3 className="font-display text-lg font-medium">DKIM</h3>
                </div>
                <StatusPill
                  ok={analysis.dkim.count > 0}
                  warn={false}
                  label={`${analysis.dkim.count} SELECTORES`}
                  testid="dns-dkim-card-status"
                />
              </div>
              {analysis.dkim.count === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Se probaron {analysis.dkim.selectors_tried.length} selectores comunes. Ninguno respondió.
                </p>
              ) : (
                <ul className="space-y-1">
                  {analysis.dkim.found.map(d => (
                    <li key={d.selector} className="flex items-center gap-2 text-xs font-mono">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      <code className="text-primary">{d.selector}</code>
                      {d.has_public_key && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">· clave presente</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <RecordBlock title="A" icon={Globe} records={analysis.records.A} testid="dns-record-a" />
            <RecordBlock title="AAAA" icon={Globe} records={analysis.records.AAAA} testid="dns-record-aaaa" />
            <RecordBlock title="MX" icon={Mail} records={analysis.records.MX} testid="dns-record-mx" />
            <RecordBlock title="NS" icon={ShieldCheck} records={analysis.records.NS} testid="dns-record-ns" />
            <RecordBlock title="CNAME" icon={Globe} records={analysis.records.CNAME} testid="dns-record-cname" />
            <RecordBlock title="SOA" icon={Fingerprint} records={analysis.records.SOA} testid="dns-record-soa" />
            <RecordBlock title="TXT" icon={Fingerprint} records={analysis.records.TXT} testid="dns-record-txt" />
            <RecordBlock title="CAA" icon={ShieldCheck} records={analysis.caa.map(c => `${c.flags} ${c.tag} "${c.value}"`)} testid="dns-record-caa" empty="sin restricciones CAA" />
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className={`border ${analysis.bimi.present ? "border-emerald-500/40" : "border-border"} bg-card/60 p-4`} data-testid="dns-bimi-card">
              <div className="overline mb-1">BIMI</div>
              <div className="font-display text-2xl">{analysis.bimi.present ? "OK" : "—"}</div>
              {analysis.bimi.record && <div className="font-mono text-[10px] mt-1 break-all">{analysis.bimi.record}</div>}
            </div>
            <div className={`border ${analysis.mta_sts.present ? "border-emerald-500/40" : "border-border"} bg-card/60 p-4`} data-testid="dns-mtasts-card">
              <div className="overline mb-1">MTA-STS</div>
              <div className="font-display text-2xl">{analysis.mta_sts.present ? "OK" : "—"}</div>
            </div>
            <div className={`border ${analysis.tls_rpt.present ? "border-emerald-500/40" : "border-border"} bg-card/60 p-4`} data-testid="dns-tlsrpt-card">
              <div className="overline mb-1">TLS-RPT</div>
              <div className="font-display text-2xl">{analysis.tls_rpt.present ? "OK" : "—"}</div>
            </div>
          </section>

          {analysis.dangling_cname && (
            <div className="border border-sev-high/40 bg-sev-high/5 p-4" data-testid="dns-dangling-cname">
              <div className="flex items-center gap-2 text-sev-high overline">
                <AlertTriangle className="w-4 h-4" /> CNAME colgando detectado
              </div>
              <p className="text-sm mt-2">
                <code className="text-xs">{analysis.domain}</code> → <code className="text-xs">{analysis.dangling_cname}</code>
                {" "}(el destino no resuelve — riesgo de subdomain takeover).
              </p>
            </div>
          )}
        </>
      )}

      {typosquat && (
        <section className="border border-border bg-card/60" data-testid="dns-typosquat-results">
          <div className="p-5 border-b border-border flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-sev-high" />
            <h2 className="font-display text-lg font-medium">Typosquatting</h2>
            <span className="ml-auto text-xs font-mono text-muted-foreground">
              {typosquat.registered} de {typosquat.generated} variantes registradas
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left overline border-b border-border">
                <th className="p-3">Dominio</th>
                <th className="p-3">A</th>
                <th className="p-3">MX</th>
                <th className="p-3">Nameservers</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {typosquat.results.length === 0 && (
                <tr><td colSpan={5} className="p-4 text-center text-muted-foreground font-mono">
                  Ninguna variante registrada · buenas noticias
                </td></tr>
              )}
              {typosquat.results.map((r) => (
                <tr key={r.domain} className="border-b border-border/50" data-row-hover data-testid={`typosquat-row-${r.domain}`}>
                  <td className="p-3 font-mono text-xs text-sev-high">{r.domain}</td>
                  <td className="p-3 font-mono text-[11px] text-muted-foreground">{(r.a || []).join(", ") || "—"}</td>
                  <td className="p-3 font-mono text-[11px] text-muted-foreground">{(r.mx || []).slice(0, 1).join(", ") || "—"}</td>
                  <td className="p-3 font-mono text-[10px] text-muted-foreground">{(r.nameservers || []).slice(0, 1).join(", ") || "—"}</td>
                  <td className="p-3">
                    <button onClick={() => copy(r.domain)} className="text-muted-foreground hover:text-foreground">
                      <Copy className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
